import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { SortMeta, FilterMatchMode } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { MonoTypeOperatorFunction, tap, filter } from 'rxjs';

import { TableFilter } from './filters';
import { patchQueryParamsFactory } from './qp-utils';

export const DEFAULT_PAGE_SIZE = 20;

export const sortKeyToSortMeta = (field: string): SortMeta =>
  field.startsWith('-') ? { field: field.slice(1), order: -1 } : { field, order: 1 };

const sortMetaToSortKey = <K>(sortMeta: SortMeta): K =>
  (sortMeta.order > 0 ? `${sortMeta.field}` : `-${sortMeta.field}`) as K;

export const qpToPaginationParams =
  (defaultLimit = DEFAULT_PAGE_SIZE) =>
    (qp: ParamMap) => ({
      offset: +(qp.get('offset') ?? 0),
      limit: +(qp.get('limit') ?? defaultLimit),
    });

export const qpToSortingParams =
  <K extends string[] | undefined>() =>
    (qp: ParamMap) => ({
      sorting: qp.getAll('sorting') as NonNullable<K>,
    });

export const qpToMultiSortMetaParams = () => (qp: ParamMap) => {
  const sorting = qp.getAll('sorting');
  return {
    multiSortMeta: sorting.length ? sorting.map(sortKeyToSortMeta) : undefined,
  };
};

export const qpToFiltersParams =
  <F extends Record<string, unknown>, K extends keyof F>(parser: (qp: ParamMap) => F, keys: K[]) =>
    (qp: ParamMap) => {
      const parsed = parser(qp);
      return {
        ...parsed,
        filters: Object.fromEntries(
          Object.entries(parsed)
            .filter((pair) => keys.includes(pair[0] as K))
            .map(([k, value]) => [
              k,
              [{ value, matchMode: Array.isArray(value) ? FilterMatchMode.IN : FilterMatchMode.IS }],
            ]),
        ) as TableFilter,
      };
    };

export const updatePaginationFactory = () => {
  const patchQueryParams = patchQueryParamsFactory();
  return (paginator: PaginatorState) =>
    patchQueryParams({
      limit: paginator.rows,
      offset: paginator.first,
    });
};

export const updateSortingFactory = () => {
  const patchQueryParams = patchQueryParamsFactory();
  return (multiSortMeta: SortMeta[] | null | undefined) =>
    patchQueryParams({
      sorting: multiSortMeta?.map(sortMetaToSortKey),
      offset: undefined,
    });
};

export const dropOffset =
  <F extends object>(serializer = (parsed: F): Params => parsed) =>
    (parsed: F): Params => ({
      ...serializer(parsed),
      offset: undefined,
    });

export const paginatorTranslateParams = (qp: { offset: number; limit: number }, count?: number) => {
  return {
    currentSet: qp.offset + qp.limit > (count ?? 0) ? count : qp.offset + qp.limit,
    total: count ?? 0,
  };
};

// TODO improve typing (sorting type check)
// currently ensured with a cast
export const withDefaultSorting = <T extends { sorting: string[] }>(
  defaultSorting: string,
  router: Router,
  route: ActivatedRoute,
): MonoTypeOperatorFunction<T> => {
  return (source$) =>
    source$.pipe(
      tap(({ sorting }) => {
        if (!sorting?.length) {
          void router.navigate([], {
            queryParams: { sorting: defaultSorting },
            replaceUrl: true,
            relativeTo: route,
          });
        }
      }),
      filter(({ sorting }) => !!sorting?.length),
    );
};
